/**
 * Wall-clock times in a named zone, for editing.
 *
 * A `datetime-local` input speaks the device's clock. An event logged at
 * 10:15 in Anaheim and corrected from a phone back in Boston must still be
 * edited as 10:15 — the clock it happened on — or saving moves it three hours.
 * These convert between an instant and the "YYYY-MM-DDTHH:mm:ss" such an input
 * holds, read in a given IANA zone (the device's when none is given).
 */

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClockIn(instant: Date, zone: string): WallClock {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
}

/** How far `zone`'s clock is ahead of UTC at `instant`, in ms. */
function offsetAt(instant: Date, zone: string): number {
  const w = wallClockIn(instant, zone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The instant as a `datetime-local` value on `zone`'s clock. */
export function toZonedInputValue(instant: Date, zone?: string | null): string {
  if (!zone) {
    const d = instant;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  const w = wallClockIn(instant, zone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}:${pad(w.second)}`;
}

/**
 * The instant a `datetime-local` value means on `zone`'s clock, or null when
 * the value doesn't parse.
 */
export function fromZonedInputValue(value: string, zone?: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return null;
  const [y, mo, d, h, mi, s] = m.slice(1).map((x) => Number(x ?? 0)) as [number, number, number, number, number, number];
  if (!zone) {
    const local = new Date(y, mo - 1, d, h, mi, s || 0);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const wall = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  // Guess with the offset at the wall time read as UTC, then correct once for
  // a DST change between the guess and the answer.
  let guess = wall - offsetAt(new Date(wall), zone);
  guess = wall - offsetAt(new Date(guess), zone);
  const out = new Date(guess);
  return Number.isNaN(out.getTime()) ? null : out;
}
